import { computed, ref, nextTick, Ref, onMounted } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { SnackbarState } from "@app/types";
import { applyHighlights } from "@app/utils/words-highlighter";
import useSnackbar from "@app/composables/use-snackbar";
import useExpansionPanel from "@app/composables/use-expansion-panel";
import { hideAll } from "tippy.js";
import { errorMessage } from "@app/utils/error-message";
import { HttpException } from "@core/exceptions/http.exception";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { useMiraIntegration } from "@app/composables/useMiraIntegration";
import useLoading from "@app/composables/use-loading";

export function useReviewItemDetails(
  reviewItem: Ref<ReviewItem>,
  expansionPanel: ReturnType<typeof useExpansionPanel>,
  expandablePanelElement: Ref<HTMLElement | undefined>,
) {
  const loading = useLoading();
  const isLoading = ref(false);
  const snackbar = useSnackbar();
  const review = injectStrict(ReviewKey);
  const isHoveredOver = ref(false);
  const isFavoriteLoading = ref(false);
  const pdfPrice = ref<number | null>(null);
  const isPdfPriceLoading = ref(false);
  const isCreateArticlePdfLoading = ref(false);
  const { isEnabled: isMiraIntegrationEnabled, load } = useMiraIntegration();

  onMounted(async () => {
    await load();
  });

  async function toggleFavorite() {
    isFavoriteLoading.value = true;
    try {
      if (reviewItem.value.isFavorite) {
        await review.unfavoriteStudy(reviewItem.value.id);
      } else {
        await review.favoriteStudy(reviewItem.value.id);
      }
      isFavoriteLoading.value = false;
      hideAll({ duration: 0 });
    } catch (e) {
      const error = e as HttpException;
      snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
      throw e;
    } finally {
      isFavoriteLoading.value = false;
    }
  }

  async function uploadStudyPdfFile(file: File) {
    try {
      await review.uploadStudyPdfFile(reviewItem.value.id, file);
      snackbar.success("Success");
    } catch (e) {
      const error = e as HttpException;
      snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
      throw e;
    }
  }

  async function getArticlePdfPrice() {
    const metadata = reviewItem.value.metadata;
    if (!metadata) return;
    if (!metadata.doi) return;

    if (reviewItem.value.cachedPdfPrice !== undefined) {
      pdfPrice.value = reviewItem.value.cachedPdfPrice;
      return;
    }

    try {
      isPdfPriceLoading.value = true;
      const price = await review.getArticlePdfPrice(reviewItem.value.id, metadata.doi);
      pdfPrice.value = price;
      reviewItem.value.cachedPdfPrice = price;
    } catch (e) {
      const error = e as HttpException;
      snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
      throw e;
    } finally {
      isPdfPriceLoading.value = false;
    }
  }

  async function createArticlePdfOrder() {
    const metadata = reviewItem.value.metadata;
    if (!metadata) return;
    if (!metadata.doi) return;

    try {
      isCreateArticlePdfLoading.value = true;
      await review.createArticlePdfOrder(reviewItem.value.id, metadata.doi);
      pdfPrice.value = -1;
      reviewItem.value.cachedPdfPrice = -1;
      snackbar.success("Success");
    } catch (e) {
      const error = e as HttpException;
      snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
      throw e;
    } finally {
      isCreateArticlePdfLoading.value = false;
    }
  }

  function isTextSelected() {
    return !!window?.getSelection()?.toString();
  }

  function expandCard() {
    if (!isTextSelected()) {
      expansionPanel.toggle();
      if (expansionPanel.isShown.value) {
        nextTick(() =>
          expandablePanelElement.value?.scrollIntoView({
            behavior: "smooth",
          }),
        );
      }
    }
  }

  const isParentStudy = computed(() => {
    return (
      review.parentStudyIds.value.has(reviewItem.value.id) ||
      review.maybeParentStudyIds.value.has(reviewItem.value.id)
    );
  });

  function onMouseOver() {
    isHoveredOver.value = true;
  }

  function onMouseOut() {
    isHoveredOver.value = false;
  }

  const formattedId = computed(() => {
    let id = reviewItem.value.id.toString();
    if (!id) return "";
    if (review.filtering.terms.value.length > 0) {
      id = applyHighlights(id, review.filtering.terms.value);
    }
    return id;
  });

  const formattedTitle = computed(() => {
    let title = reviewItem.value.metadata?.title;
    if (!title) return "";
    if (review.filtering.terms.value.length > 0) {
      title = applyHighlights(title, review.filtering.terms.value);
    }
    return title;
  });

  return {
    review,
    loading,
    isLoading,
    isFavoriteLoading,
    snackbar,
    isHoveredOver,
    isParentStudy,
    formattedId,
    formattedTitle,
    pdfPrice,
    isPdfPriceLoading,
    isCreateArticlePdfLoading,
    isMiraIntegrationEnabled,
    toggleFavorite,
    uploadStudyPdfFile,
    getArticlePdfPrice,
    createArticlePdfOrder,
    expandCard,
    onMouseOver,
    onMouseOut,
  };
}
